import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import {
  TocSidebar,
  ReaderFooter,
  ReaderDocument,
  useSelectionCapture,
  useHighlightPainter,
} from '@arianna/reader-ui'
import type { RenderBlockActions } from '@arianna/reader-ui'
import {
  HIGHLIGHT_COLORS,
  HIGHLIGHT_STYLES,
  type ReaderRenderHighlight,
  type ReaderRenderHighlightColor,
  type ReaderRenderHighlightStyle,
} from '@arianna/reader-render'
import {
  buildTocAdapter,
  buildCurrentSectionAdapter,
  findRouteSectionBySlug,
  EMPTY_MANUALE,
} from './adapters.js'
import { idsForProgressFlag } from '@arianna/reader-core'
import { syncHighlights, deleteHighlight, syncProgress, syncReadingPosition } from './syncApi.js'
import CourseLayout from './CourseLayout.js'
import { sectionUrl, quizSessionUrl } from './urls.js'
import type { PageComponent } from './inertiaPage.js'
import type { LetturaPageProps } from './types.js'
import { TutorDrawer } from './tutor/TutorDrawer.js'
import { useTutorDrawerState } from './tutor/useTutorDrawerState.js'
import { resolveTutorSectionTarget } from './tutorNav.js'
import { BlockActionsMenu } from './BlockActionsMenu.js'

// ---------------------------------------------------------------------------
// Highlight toolbar (appears on selection) — unchanged from the fat client,
// pure presentational, no manuale/bundle dependency.
// ---------------------------------------------------------------------------

interface HighlightToolbarProps {
  onPickColor: (color: ReaderRenderHighlightColor, style: ReaderRenderHighlightStyle) => void
  onDelete: () => void
  canDelete: boolean
  style?: React.CSSProperties
}

function HighlightToolbar({ onPickColor, onDelete, canDelete, style }: HighlightToolbarProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-2) var(--space-3)',
        boxShadow: 'var(--shadow-md)',
        ...style,
      }}
    >
      <div style={{ display: 'grid', gap: 6 }}>
        {HIGHLIGHT_STYLES.map(({ style: highlightStyle, label }) => (
          <div
            key={highlightStyle}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
          >
            <span style={{ width: 16, color: 'var(--ink-3)', fontSize: 12 }}>
              {highlightStyle === 'mark' ? '▰' : '_'}
            </span>
            {HIGHLIGHT_COLORS.map(({ color, label: colorLabel, hex }) => (
              <button
                key={`${highlightStyle}-${color}`}
                title={`${label} ${colorLabel}`}
                onClick={() => onPickColor(color, highlightStyle)}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: highlightStyle === 'mark' ? '50%' : 5,
                  background: highlightStyle === 'mark' ? hex : 'transparent',
                  border:
                    highlightStyle === 'mark' ? '2px solid var(--line-2)' : '1px solid var(--line)',
                  borderBottom: `3px solid ${hex}`,
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'transform 0.1s ease',
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.16)'
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
                }}
              />
            ))}
          </div>
        ))}
      </div>
      {canDelete && (
        <>
          <div
            style={{ width: 1, height: 44, background: 'var(--line)', margin: '0 var(--space-1)' }}
          />
          <button
            onClick={onDelete}
            title="Rimuovi highlight"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3)',
              fontSize: 18,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              padding: 2,
            }}
          >
            ×
          </button>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lettura — the reading page (TASK-009 Phase 5 thin client, TASK-014 course
// shell). Reading chrome (TOC sidebar, highlight toolbar, drawer tutor,
// completed/review toggles, prev/next) lives ONLY here — see task doc
// "Reading chrome" definition. Product navigation (Quiz/Padronanza/Tutor
// full-screen/Da rivedere panel) moved to CourseTabs + their own pages;
// removed from here entirely (TASK-014).
// ---------------------------------------------------------------------------

const Lettura: PageComponent<LetturaPageProps> = ({
  shell,
  toc: tocProp,
  readingState,
  section,
}) => {
  const { manualVersionId } = shell

  const tocAdapter = useMemo(() => buildTocAdapter(tocProp), [tocProp])

  // ---------------------------------------------------------------------------
  // Reading state — re-seeded from `readingState` on every fresh Inertia visit
  // (a full navigation always carries the current authoritative snapshot).
  // Uses React's documented "adjusting state when a prop changes" render-phase
  // pattern (not a useEffect) — comparing against the last-seen `readingState`
  // reference and calling setState during render bails out before committing
  // the stale intermediate render, avoiding an extra frame and the
  // react-hooks/set-state-in-effect footgun.
  // ---------------------------------------------------------------------------
  const [highlights, setHighlights] = useState<ReaderRenderHighlight[]>(readingState.highlights)
  const [completedIds, setCompletedIds] = useState<Set<string>>(() =>
    idsForProgressFlag(readingState.progress, 'completed'),
  )
  const [reviewIds, setReviewIds] = useState<Set<string>>(() =>
    idsForProgressFlag(readingState.progress, 'review'),
  )
  const [seenReadingState, setSeenReadingState] = useState(readingState)
  if (readingState !== seenReadingState) {
    setSeenReadingState(readingState)
    setHighlights(readingState.highlights)
    setCompletedIds(idsForProgressFlag(readingState.progress, 'completed'))
    setReviewIds(idsForProgressFlag(readingState.progress, 'review'))
  }

  const [tocOpen, setTocOpen] = useState(true)

  // "Chat sul blocco" side drawer — see ./tutor/useTutorDrawerState.ts for
  // the lifecycle contract (minimize preserves state; a new chat-from-block
  // replaces the drawer via a fresh `key`; navigation drops it — reset
  // effect below).
  const {
    drawerChat,
    drawerMinimized,
    openForBlock: openTutorDrawerForBlock,
    minimize: minimizeTutorDrawer,
    restore: restoreTutorDrawer,
    resetForNavigation: resetTutorDrawerForNavigation,
  } = useTutorDrawerState()

  // ---------------------------------------------------------------------------
  // Current section adapter
  // ---------------------------------------------------------------------------
  const currentSectionAdapter = useMemo(
    () => buildCurrentSectionAdapter(tocAdapter, section),
    [tocAdapter, section],
  )
  const prevRouteSection = useMemo(
    () => findRouteSectionBySlug(tocAdapter, section.prev_slug),
    [tocAdapter, section],
  )
  const nextRouteSection = useMemo(
    () => findRouteSectionBySlug(tocAdapter, section.next_slug),
    [tocAdapter, section],
  )
  const currentIdx = useMemo(
    () => tocAdapter.flatSections.findIndex((s) => s.slug === section.slug),
    [tocAdapter, section],
  )

  const isCompleted =
    currentSectionAdapter.subParaIds.length > 0 &&
    currentSectionAdapter.subParaIds.every((id) => completedIds.has(id))
  const isReview = currentSectionAdapter.subParaIds.some((id) => reviewIds.has(id))

  // ---------------------------------------------------------------------------
  // Navigation — Inertia visits on server-side routes, no client slug map.
  // ---------------------------------------------------------------------------
  const goToSlug = useCallback(
    (slug: string) => router.visit(sectionUrl(manualVersionId, slug)),
    [manualVersionId],
  )
  const goPrev = useCallback(() => {
    if (section.prev_slug != null) goToSlug(section.prev_slug)
  }, [section, goToSlug])
  const goNext = useCallback(() => {
    if (section.next_slug != null) goToSlug(section.next_slug)
  }, [section, goToSlug])
  const handleTocSelect = useCallback(
    (idx: number) => {
      const target = tocAdapter.flatSections[idx]
      if (target != null) goToSlug(target.slug)
    },
    [tocAdapter, goToSlug],
  )

  // Prefetch adjacent sections (TASK-009 Phase 5 — mitigates per-navigation latency).
  useEffect(() => {
    if (section.next_slug != null) {
      router.prefetch(
        sectionUrl(manualVersionId, section.next_slug),
        { method: 'get' },
        { cacheFor: '30s' },
      )
    }
    if (section.prev_slug != null) {
      router.prefetch(
        sectionUrl(manualVersionId, section.prev_slug),
        { method: 'get' },
        { cacheFor: '30s' },
      )
    }
  }, [manualVersionId, section])

  // Sync reading position on every section change.
  useEffect(() => {
    void syncReadingPosition(manualVersionId, section.section_id)
  }, [manualVersionId, section])

  // "Navigazione = drawer perso": an Inertia `router.visit` RE-RENDERS this
  // same `Lettura` component — it does NOT unmount/remount it, so the drawer
  // state would otherwise survive a section change on its own. Reset
  // explicitly whenever the current section identity changes (no-op if
  // there's no drawer).
  useEffect(() => {
    resetTutorDrawerForNavigation()
  }, [section.section_id, resetTutorDrawerForNavigation])

  // ---------------------------------------------------------------------------
  // Content pane ref + scroll-to-top / scroll-to-anchor on section change.
  // A URL fragment (e.g. `#1-1-1-b2` from a mastery "Ripassa" deep-link or a
  // TASK-010 citation link) scrolls to that block/sub-paragraph anchor
  // (see packages/reader-ui/src/ReaderDocument.tsx `id={blockDomId(...)}`)
  // instead of resetting to the top.
  // ---------------------------------------------------------------------------
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null)
  const contentCallbackRef = useCallback((el: HTMLDivElement | null) => {
    contentRef.current = el
    setContentEl(el)
  }, [])

  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return
    const hash = window.location.hash.replace(/^#/, '')
    if (hash.length > 0) {
      const target = document.getElementById(hash)
      if (target != null) {
        const reduceMotion =
          typeof window !== 'undefined' &&
          window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
        return
      }
    }
    el.scrollTop = 0
  }, [section.slug])

  // ---------------------------------------------------------------------------
  // Selection + highlight toolbar state
  // ---------------------------------------------------------------------------
  const [pendingSelection, setPendingSelection] = useState<{
    subParaId: string
    blockId: string
    startChar: number
    endChar: number
    text: string
  } | null>(null)
  const [toolbarVisible, setToolbarVisible] = useState(false)
  const [toolbarPos, setToolbarPos] = useState<{ x: number; y: number } | null>(null)

  useSelectionCapture({
    container: contentEl,
    onSelection: (range) => {
      setPendingSelection(range)
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const rect = sel.getRangeAt(0).getBoundingClientRect()
        setToolbarPos({ x: rect.left + rect.width / 2, y: rect.bottom })
      }
      setToolbarVisible(true)
    },
    onCleared: () => {
      setToolbarVisible(false)
      setPendingSelection(null)
      setToolbarPos(null)
    },
  })

  useHighlightPainter({
    container: contentEl,
    highlights,
    sectionKey: section.section_id,
  })

  const selectionOverlapsHighlight = useCallback(
    (h: ReaderRenderHighlight) =>
      pendingSelection != null &&
      h.blockId === pendingSelection.blockId &&
      h.startChar < pendingSelection.endChar &&
      h.endChar > pendingSelection.startChar,
    [pendingSelection],
  )
  const selectionHasHighlights =
    pendingSelection != null && highlights.some(selectionOverlapsHighlight)

  const handlePickColor = useCallback(
    async (color: ReaderRenderHighlightColor, style: ReaderRenderHighlightStyle) => {
      if (!pendingSelection) return
      setToolbarVisible(false)
      setPendingSelection(null)
      setToolbarPos(null)
      window.getSelection()?.removeAllRanges()

      const existing = highlights.find(
        (h) =>
          h.blockId === pendingSelection.blockId &&
          h.startChar === pendingSelection.startChar &&
          h.endChar === pendingSelection.endChar,
      )

      if (existing != null) {
        if (existing.color === color && (existing.style ?? 'mark') === style) {
          setHighlights(highlights.filter((h) => h.id !== existing.id))
          await deleteHighlight(manualVersionId, existing.id)
          return
        }
        const updated: ReaderRenderHighlight = { ...existing, color, style }
        setHighlights(highlights.map((h) => (h.id === existing.id ? updated : h)))
        const merged = await syncHighlights(manualVersionId, [updated])
        setHighlights(merged)
        return
      }

      const newHighlight: ReaderRenderHighlight = {
        id: crypto.randomUUID(),
        subParaId: pendingSelection.subParaId,
        blockId: pendingSelection.blockId,
        startChar: pendingSelection.startChar,
        endChar: pendingSelection.endChar,
        color,
        style,
      }
      setHighlights([...highlights, newHighlight])
      const merged = await syncHighlights(manualVersionId, [newHighlight])
      setHighlights(merged)
    },
    [pendingSelection, highlights, manualVersionId],
  )

  const handleDeleteHighlight = useCallback(async () => {
    if (!pendingSelection) return
    const matches = highlights.filter(
      (h) =>
        h.blockId === pendingSelection.blockId &&
        h.startChar < pendingSelection.endChar &&
        h.endChar > pendingSelection.startChar,
    )
    setToolbarVisible(false)
    setPendingSelection(null)
    setToolbarPos(null)
    window.getSelection()?.removeAllRanges()
    if (matches.length === 0) return
    const matchIds = new Set(matches.map((h) => h.id))
    setHighlights(highlights.filter((h) => !matchIds.has(h.id)))
    await Promise.all(matches.map((h) => deleteHighlight(manualVersionId, h.id)))
  }, [pendingSelection, highlights, manualVersionId])

  // ---------------------------------------------------------------------------
  // Completion / review toggles — use the CURRENT section's REAL subParaIds
  // (section.content.parts[].id). Sections other than the current one are
  // known only via the server-precomputed completed/review booleans (toc
  // prop) — see adapters.ts doc for why real per-section subParaIds aren't
  // available for those. After a successful sync, a partial Inertia reload
  // refreshes `toc` so the sidebar/progress label pick up the new flag
  // without a full navigation.
  // ---------------------------------------------------------------------------

  const toggleCompletion = useCallback(async () => {
    const ids = currentSectionAdapter.subParaIds
    const allCompleted = ids.every((id) => completedIds.has(id))
    const next = new Set(completedIds)
    ids.forEach((id) => (allCompleted ? next.delete(id) : next.add(id)))
    setCompletedIds(next)
    await syncProgress(
      manualVersionId,
      ids.map((id) => ({
        sub_para_id: id,
        completed: !allCompleted,
        scroll_pos: null,
        review: reviewIds.has(id),
      })),
    )
    router.reload({ only: ['toc'] })
  }, [currentSectionAdapter, completedIds, reviewIds, manualVersionId])

  const toggleReview = useCallback(async () => {
    const ids = currentSectionAdapter.subParaIds
    const allReview = ids.every((id) => reviewIds.has(id))
    const next = new Set(reviewIds)
    ids.forEach((id) => (allReview ? next.delete(id) : next.add(id)))
    setReviewIds(next)
    await syncProgress(
      manualVersionId,
      ids.map((id) => ({
        sub_para_id: id,
        completed: completedIds.has(id),
        scroll_pos: null,
        review: !allReview,
      })),
    )
    router.reload({ only: ['toc'] })
  }, [currentSectionAdapter, reviewIds, completedIds, manualVersionId])

  // ---------------------------------------------------------------------------
  // Tutor drawer (TASK-010 Phase 3) — "Chat sul blocco" side panel. The
  // full-screen tutor overlay this page used to also render is gone
  // (TASK-014 — Tutor is now its own page, see pages/reader/Tutor.tsx); this
  // drawer is the ONLY tutor surface left inside Lettura.
  // ---------------------------------------------------------------------------

  const handleTutorResolveSectionTarget = useCallback(
    (sectionId: string, fineId?: string | null) =>
      resolveTutorSectionTarget(tocAdapter, sectionId, fineId),
    [tocAdapter],
  )

  const handleOpenTutorForBlock = useCallback(
    (args: { blockId: string; subParaId: string }) => {
      openTutorDrawerForBlock(args)
    },
    [openTutorDrawerForBlock],
  )

  const renderBlockActions = useCallback<RenderBlockActions>(
    ({ block, subParaId }) => (
      <BlockActionsMenu block={block} subParaId={subParaId} onChat={handleOpenTutorForBlock} />
    ),
    [handleOpenTutorForBlock],
  )

  const handleTutorDrawerNavigateToSection = useCallback(
    (slug: string, anchor: string) => {
      router.visit(`${sectionUrl(manualVersionId, slug)}${anchor}`)
    },
    [manualVersionId],
  )

  // Launching a quiz from the drawer now navigates to the session's own URL
  // (TASK-014 — quiz is a real page, not client overlay state) instead of
  // opening the removed QuizOverlay in place.
  const handleTutorDrawerLaunchQuiz = useCallback(
    (quizSessionId: string) => {
      router.visit(quizSessionUrl(manualVersionId, quizSessionId))
    },
    [manualVersionId],
  )

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* TASK-014 dedup: no reading top-bar at all. Everything it carried
          lives elsewhere now — title in CourseLayout's header, theme toggle
          in the header actions, prev/next + completed/review in ReaderFooter,
          section counter in the TOC sidebar. The only orphan, the TOC
          toggle, floats over the content pane below. */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {tocOpen && (
          <TocSidebar
            sections={tocAdapter.routeSections}
            parents={tocAdapter.routeParents}
            currentIdx={currentIdx}
            completedIds={tocAdapter.completedIds}
            reviewIds={tocAdapter.reviewIds}
            onSelect={handleTocSelect}
          />
        )}

        {/* Right area — content pane (scrolls) + footer pinned to its bottom.
            A flex column inside the row so the footer spans ONLY the manual
            column, not the full width under the TOC sidebar (which now runs
            full height on the left). The footer is a flex sibling of the
            scroll pane, not overlaid, so it's always visible and never
            covers the last lines of text. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            ref={contentCallbackRef}
            style={{ flex: 1, position: 'relative', overflowY: 'auto' }}
          >
            {/* Floating TOC toggle — sticky so it stays visible while the
                content scrolls; zero-height wrapper so it takes no layout
                space. Same hamburger glyph the old top-bar toggle used. */}
            <div style={{ position: 'sticky', top: 12, height: 0, zIndex: 60, paddingLeft: 12 }}>
              <button
                onClick={() => setTocOpen((o) => !o)}
                title={tocOpen ? 'Nascondi indice' : 'Mostra indice'}
                aria-pressed={tocOpen}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  boxShadow: 'var(--shadow-sm)',
                  color: 'var(--ink-2)',
                  cursor: 'pointer',
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 22 22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="4" y1="6" x2="18" y2="6" />
                  <line x1="4" y1="11" x2="18" y2="11" />
                  <line x1="4" y1="16" x2="13" y2="16" />
                </svg>
              </button>
            </div>
            <ReaderDocument
              manuale={EMPTY_MANUALE}
              section={currentSectionAdapter.routeSection}
              loadedSection={currentSectionAdapter.loadedSection}
              renderBlockActions={renderBlockActions}
            />

            {toolbarVisible && pendingSelection && toolbarPos && (
              <div
                style={{
                  position: 'fixed',
                  top: toolbarPos.y + 8,
                  left: toolbarPos.x,
                  transform: 'translateX(-50%)',
                  zIndex: 200,
                  maxWidth: 'calc(100vw - 32px)',
                }}
              >
                <HighlightToolbar
                  onPickColor={(color, style) => void handlePickColor(color, style)}
                  onDelete={() => void handleDeleteHighlight()}
                  canDelete={selectionHasHighlights}
                />
              </div>
            )}
          </div>

          <ReaderFooter
            prevSection={prevRouteSection}
            nextSection={nextRouteSection}
            isCompleted={isCompleted}
            isReview={isReview}
            onPrev={goPrev}
            onNext={goNext}
            onToggleCompleted={() => void toggleCompletion()}
            onToggleReview={() => void toggleReview()}
          />
        </div>

        {drawerChat != null && (
          <TutorDrawer
            key={drawerChat.key}
            manualVersionId={manualVersionId}
            focusBlock={drawerChat.focusBlock}
            minimized={drawerMinimized}
            onMinimize={minimizeTutorDrawer}
            onRestore={restoreTutorDrawer}
            resolveSectionTarget={handleTutorResolveSectionTarget}
            onNavigateToSection={handleTutorDrawerNavigateToSection}
            onLaunchQuiz={handleTutorDrawerLaunchQuiz}
          />
        )}
      </div>
    </div>
  )
}

Lettura.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default Lettura
